/**
 * @file Components/Transport/LocationAutocomplete.jsx
 * @description MakeMyTrip-style location autocomplete input.
 * Shows city/airport suggestions with IATA codes as user types.
 * Uses debounced API calls + local fallback for instant results.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Plane, Building2, Search, Loader2, X } from 'lucide-react';
import { searchLocations } from '../../services/api';

// ─── Popular cities for instant suggestions ────────────────────────
const POPULAR_CITIES = [
  { code: 'DEL', name: 'New Delhi', city: 'Delhi', country: 'India', type: 'airport', airportName: 'Indira Gandhi International' },
  { code: 'BOM', name: 'Mumbai', city: 'Mumbai', country: 'India', type: 'airport', airportName: 'Chhatrapati Shivaji Maharaj' },
  { code: 'BLR', name: 'Bengaluru', city: 'Bangalore', country: 'India', type: 'airport', airportName: 'Kempegowda International' },
  { code: 'MAA', name: 'Chennai', city: 'Chennai', country: 'India', type: 'airport', airportName: 'Chennai International' },
  { code: 'CCU', name: 'Kolkata', city: 'Kolkata', country: 'India', type: 'airport', airportName: 'Netaji Subhas Chandra Bose' },
  { code: 'HYD', name: 'Hyderabad', city: 'Hyderabad', country: 'India', type: 'airport', airportName: 'Rajiv Gandhi International' },
  { code: 'GOI', name: 'Goa', city: 'Goa', country: 'India', type: 'airport', airportName: 'Dabolim Airport' },
  { code: 'JAI', name: 'Jaipur', city: 'Jaipur', country: 'India', type: 'airport', airportName: 'Jaipur International' },
  { code: 'PNQ', name: 'Pune', city: 'Pune', country: 'India', type: 'airport', airportName: 'Pune Airport' },
  { code: 'AMD', name: 'Ahmedabad', city: 'Ahmedabad', country: 'India', type: 'airport', airportName: 'Sardar Vallabhbhai Patel' },
  { code: 'LKO', name: 'Lucknow', city: 'Lucknow', country: 'India', type: 'airport', airportName: 'Chaudhary Charan Singh' },
  { code: 'COK', name: 'Kochi', city: 'Kochi', country: 'India', type: 'airport', airportName: 'Cochin International' },
  { code: 'SXR', name: 'Srinagar', city: 'Srinagar', country: 'India', type: 'airport', airportName: 'Sheikh ul-Alam International' },
  { code: 'IXL', name: 'Leh', city: 'Leh', country: 'India', type: 'airport', airportName: 'Kushok Bakula Rimpochee' },
  { code: 'VNS', name: 'Varanasi', city: 'Varanasi', country: 'India', type: 'airport', airportName: 'Lal Bahadur Shastri' },
  { code: 'ATQ', name: 'Amritsar', city: 'Amritsar', country: 'India', type: 'airport', airportName: 'Sri Guru Ram Dass Jee' },
  { code: 'IXC', name: 'Chandigarh', city: 'Chandigarh', country: 'India', type: 'airport', airportName: 'Chandigarh Airport' },
  { code: 'DED', name: 'Dehradun', city: 'Dehradun', country: 'India', type: 'airport', airportName: 'Jolly Grant Airport' },
  { code: 'IXB', name: 'Bagdogra', city: 'Siliguri', country: 'India', type: 'airport', airportName: 'Bagdogra Airport' },
  { code: 'IXZ', name: 'Port Blair', city: 'Andaman', country: 'India', type: 'airport', airportName: 'Veer Savarkar International' },
  { code: 'UDR', name: 'Udaipur', city: 'Udaipur', country: 'India', type: 'airport', airportName: 'Maharana Pratap Airport' },
  { code: 'DXB', name: 'Dubai', city: 'Dubai', country: 'UAE', type: 'airport', airportName: 'Dubai International' },
  { code: 'SIN', name: 'Singapore', city: 'Singapore', country: 'Singapore', type: 'airport', airportName: 'Changi Airport' },
  { code: 'BKK', name: 'Bangkok', city: 'Bangkok', country: 'Thailand', type: 'airport', airportName: 'Suvarnabhumi Airport' },
  { code: 'LHR', name: 'London', city: 'London', country: 'UK', type: 'airport', airportName: 'Heathrow Airport' },
];

// ─── Bus/Cab cities (no IATA needed) ────────────────────────────────
const BUS_CAB_CITIES = [
  'Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad',
  'Jaipur', 'Lucknow', 'Chandigarh', 'Goa', 'Indore', 'Bhopal', 'Nagpur', 'Surat',
  'Vadodara', 'Kochi', 'Thiruvananthapuram', 'Coimbatore', 'Madurai', 'Mangalore',
  'Mysore', 'Vizag', 'Bhubaneswar', 'Patna', 'Ranchi', 'Guwahati', 'Dehradun',
  'Shimla', 'Manali', 'Rishikesh', 'Haridwar', 'Varanasi', 'Agra', 'Mathura',
  'Amritsar', 'Jammu', 'Udaipur', 'Jodhpur', 'Jaisalmer', 'Mount Abu', 'Ajmer',
  'Pushkar', 'Raipur', 'Nashik', 'Aurangabad', 'Shirdi', 'Lonavala', 'Mahabaleshwar',
  'Ooty', 'Kodaikanal', 'Munnar', 'Alleppey', 'Pondicherry', 'Tirupati', 'Hampi',
  'Darjeeling', 'Gangtok', 'Shillong', 'Leh', 'Srinagar', 'Mcleodganj', 'Kasol',
];

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const LocationAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Enter city or airport',
  label = 'From',
  mode = 'flight', // 'flight' | 'bus' | 'cab'
  className = '',
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const debouncedInput = useDebounce(inputValue, 250);

  // Sync external value
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Close dropdown on outside click (check both wrapper AND portal dropdown)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inWrapper && !inDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions
  useEffect(() => {
    if (!debouncedInput || debouncedInput.length < 2) {
      setSuggestions([]);
      return;
    }

    const query = debouncedInput.toLowerCase();

    if (mode === 'flight') {
      // Local filter first for instant results
      const local = POPULAR_CITIES.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.city.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query) ||
        c.airportName.toLowerCase().includes(query)
      );
      setSuggestions(local);

      // Then try Amadeus API for more results
      setLoading(true);
      searchLocations(debouncedInput)
        .then(res => {
          const apiResults = (res?.data?.data?.locations || []).map(loc => ({
            code: loc.iataCode,
            name: loc.name || loc.address?.cityName || '',
            city: loc.address?.cityName || '',
            country: loc.address?.countryCode || '',
            type: loc.subType?.toLowerCase() || 'airport',
            airportName: loc.name || '',
          }));

          if (apiResults.length > 0) {
            // Merge: API results first, then local ones not already in API
            const apiCodes = new Set(apiResults.map(r => r.code));
            const merged = [...apiResults, ...local.filter(l => !apiCodes.has(l.code))];
            setSuggestions(merged.slice(0, 10));
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      // Bus/Cab: local city search only
      const filtered = BUS_CAB_CITIES
        .filter(c => c.toLowerCase().includes(query))
        .map(c => ({ code: '', name: c, city: c, country: 'India', type: 'city' }));
      setSuggestions(filtered.slice(0, 8));
    }
  }, [debouncedInput, mode]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange?.(val);
    setShowDropdown(true);
  };

  const handleSelect = (item) => {
    if (mode === 'flight') {
      setInputValue(`${item.city || item.name} (${item.code})`);
      onSelect?.({ code: item.code, name: item.city || item.name, displayName: `${item.city || item.name} (${item.code})` });
      onChange?.(`${item.city || item.name} (${item.code})`);
    } else {
      setInputValue(item.name);
      onSelect?.({ code: '', name: item.name, displayName: item.name });
      onChange?.(item.name);
    }
    setShowDropdown(false);
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.('');
    onSelect?.(null);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setFocused(true);
    setShowDropdown(true);
    if (!inputValue) {
      // Show popular cities on focus
      if (mode === 'flight') {
        setSuggestions(POPULAR_CITIES.slice(0, 8));
      } else {
        setSuggestions(BUS_CAB_CITIES.slice(0, 8).map(c => ({ code: '', name: c, city: c, country: 'India', type: 'city' })));
      }
    }
  };

  // Calculate dropdown position from wrapper ref
  const getDropdownStyle = () => {
    if (!wrapperRef.current) return {};
    const rect = wrapperRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 340),
      zIndex: 9999,
    };
  };

  const dropdownContent = showDropdown && suggestions.length > 0 && (
    <div ref={dropdownRef} style={getDropdownStyle()}
      className="bg-white rounded-xl shadow-2xl border border-gray-100 max-h-[360px] overflow-y-auto">
      
      {/* Header */}
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 rounded-t-xl">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          {inputValue?.length >= 2 ? 'Search Results' : 'Popular Cities'}
        </p>
      </div>

      {/* Suggestions */}
      {suggestions.map((item, idx) => (
        <button
          key={`${item.code}-${item.name}-${idx}`}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSelect(item)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors text-left border-b border-gray-50 last:border-0 group"
        >
          {/* Icon */}
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
            item.type === 'airport' || mode === 'flight'
              ? 'bg-blue-50 text-blue-500 group-hover:bg-blue-100'
              : 'bg-orange-50 text-orange-500 group-hover:bg-orange-100'
          }`}>
            {mode === 'flight' ? <Plane size={16} /> : <Building2 size={16} />}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900 truncate">{item.city || item.name}</span>
              {item.code && (
                <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex-shrink-0">{item.code}</span>
              )}
            </div>
            {item.airportName && mode === 'flight' && (
              <p className="text-[11px] text-gray-400 truncate mt-0.5">{item.airportName}, {item.country}</p>
            )}
            {mode !== 'flight' && (
              <p className="text-[11px] text-gray-400 mt-0.5">{item.country}</p>
            )}
          </div>

          {/* Arrow */}
          <MapPin size={14} className="text-gray-200 group-hover:text-indigo-400 flex-shrink-0 transition-colors" />
        </button>
      ))}
    </div>
  );

  const noResultsContent = showDropdown && inputValue?.length >= 2 && suggestions.length === 0 && !loading && (
    <div style={getDropdownStyle()} className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 text-center">
      <Search size={24} className="mx-auto text-gray-300 mb-2" />
      <p className="text-sm text-gray-500">No cities found for "<span className="font-semibold">{inputValue}</span>"</p>
      <p className="text-xs text-gray-400 mt-1">Try a different spelling</p>
    </div>
  );

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative transition-all">
        <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">{label}</label>
        <div className="flex items-center gap-2 mt-1">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder={placeholder}
            className="w-full text-xl font-bold text-gray-900 bg-transparent outline-none placeholder:text-gray-300 placeholder:font-normal placeholder:text-base"
            autoComplete="off"
          />
          {inputValue && (
            <button type="button" onClick={handleClear} className="text-gray-300 hover:text-gray-500 transition-colors p-0.5">
              <X size={16} />
            </button>
          )}
          {loading && <Loader2 size={16} className="text-indigo-400 animate-spin flex-shrink-0" />}
        </div>
        {inputValue && mode === 'flight' && (
          <p className="text-xs text-gray-400 mt-0.5">{inputValue}</p>
        )}
      </div>

      {/* Portal dropdowns — rendered on document.body to escape overflow clipping */}
      {dropdownContent && createPortal(dropdownContent, document.body)}
      {noResultsContent && createPortal(noResultsContent, document.body)}
    </div>
  );
};

export default LocationAutocomplete;
